import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { ChatMessage, ChatState, ChatView, Entrada } from './types';

/**
 * Origem da visita. Na página própria vem da URL; no balão, o widget repassa na URL do iframe
 * os UTMs e o endereço da página do site onde o chat foi aberto (`page`).
 */
function attribution() {
  const p = new URLSearchParams(window.location.search);
  const out: Record<string, string> = {};
  for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid']) {
    const v = p.get(k);
    if (v) out[k] = v.slice(0, 200);
  }
  const page = p.get('page');
  if (page) out.page = page.slice(0, 500);
  const ref = p.get('ref') ?? document.referrer;
  if (ref) out.referrer = ref.slice(0, 500);
  return out;
}

export function useChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [estado, setEstado] = useState<ChatState | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  const apply = (v: ChatView) => {
    setMessages(v.messages);
    setEstado(v.estado);
  };

  const start = useCallback(async (reiniciar = false) => {
    setLoading(true);
    setError(null);
    try {
      apply(await api.post<ChatView>('/chat/start', { ...attribution(), ...(reiniciar ? { reiniciar: true } : {}) }));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Não consegui abrir a conversa. Tente de novo.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void start();
  }, [start]);

  const send = useCallback(
    async (entrada: Entrada, rotuloOtimista?: string) => {
      if (sending) return;
      setSending(true);
      setError(null);
      if (rotuloOtimista)
        setMessages((m) => [...m.map((x) => ({ ...x, chips: undefined })), { id: `tmp-${Date.now()}`, de: 'pessoa', texto: rotuloOtimista }]);
      try {
        apply(await api.post<ChatView>('/chat/message', entrada));
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Não consegui enviar. Tente de novo.');
        // Recarrega o estado verdadeiro: a mensagem otimista pode não ter sido gravada.
        try {
          apply(await api.get<ChatView>('/chat/state'));
        } catch {
          /* mantém o que está na tela */
        }
      } finally {
        setSending(false);
      }
    },
    [sending],
  );

  return {
    messages,
    estado,
    loading,
    sending,
    error,
    send,
    restart: () => start(true),
    setLink: (link: string) => setEstado((e) => (e ? { ...e, linkWhatsapp: link } : e)),
  };
}
